/**
 * Times of day — one clock convention, chosen from the browser locale.
 *
 * Only US English gets a 12-hour clock with AM/PM; every other locale gets
 * 24-hour "18:30" (DS-3: one format, one exception). Live test 9 Oct 2026 (finding 22): an Irish owner saw
 * reminder and glucose times as "6:30 PM". The rule mirrors the unit defaults in
 * `unitDefaultsForLocale` — en-US is the exception, everyone else the default.
 *
 * Storage never changes: every time is held and sent as 24-hour "HH:MM". This
 * module only decides how a time is SHOWN and how the time selects are laid out.
 */

import i18n from '../i18n';

/** True when the locale reads times on a 12-hour clock — US English only. */
export function uses12HourClock(
  locale: string | undefined | null = typeof navigator !== 'undefined' ? navigator.language : undefined,
): boolean {
  return (locale ?? '').replace('_', '-').toLowerCase() === 'en-us';
}

/**
 * Format an hour (0–23) and minute for display: "6:30 PM" for en-US, "18:30"
 * everywhere else.
 */
export function formatClock(hour24: number, minute: number, twelveHour = uses12HourClock()): string {
  const mm = String(minute).padStart(2, '0');
  if (!twelveHour) return `${String(hour24).padStart(2, '0')}:${mm}`;
  const period = hour24 < 12 ? i18n.t('time.am') : i18n.t('time.pm');
  const displayHour = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${displayHour}:${mm} ${period}`;
}

/** Format a stored "HH:MM" time; returns null when it can't be parsed. */
export function formatClockString(hhmm: string | null | undefined, twelveHour = uses12HourClock()): string | null {
  const match = /^(\d{1,2}):(\d{2})/.exec((hhmm ?? '').trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  return formatClock(hour, minute, twelveHour);
}

/** Format the time-of-day of a Date (local wall clock). */
export function formatTimeOfDay(date: Date, twelveHour = uses12HourClock()): string {
  return formatClock(date.getHours(), date.getMinutes(), twelveHour);
}
